import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API_BASE_URL, ENDPOINTS } from './config';

export default function AddApplicant() {
  const navigate = useNavigate();
  const { jobId } = useParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [jobDetails, setJobDetails] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [skillsInput, setSkillsInput] = useState('');
  const [extractingFields, setExtractingFields] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    resume_url: '',
    resume_text: '',
    skills: [],
    experience_years: '',
    education: '',
    location: '',
    notes: ''
  });

  //TODO: This can become slow when we fetch all job postings every time. We should create a separate endpoint for fetching JobPosting details.
  // Add useEffect to fetch job details
  useEffect(() => {
    const fetchJobDetails = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_BASE_URL}${ENDPOINTS.JOBS}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          }
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.error || "Failed to fetch");
        
        // Find the specific job
        const job = data.find(j => j.job_id === jobId);
        if (!job) throw new Error("Job posting not found");
        
        setJobDetails(job);
      } catch (err) {
        setError(err.message);
      }
    };

    fetchJobDetails();
  }, [jobId]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle skills input (comma-separated)
  const handleSkillsChange = (e) => {
    const value = e.target.value;
    setSkillsInput(value);
    // Update the skills array in formData
    setFormData(prev => ({
      ...prev,
      skills: value.split(',').map(skill => skill.trim()).filter(skill => skill.length > 0)
    }));
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      // Clear the resume_url since we'll be uploading a new file
      setFormData(prev => ({ ...prev, resume_url: '' }));
    }
  };

  // Handle extract fields from resume
  const handleExtractFields = async () => {
    if (!selectedFile) {
      setError('Please select a resume file first');
      return;
    }

    setExtractingFields(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const formDataToSend = new FormData();
      formDataToSend.append('resume', selectedFile);

      const response = await fetch(`${API_BASE_URL}${ENDPOINTS.EXTRACT_FIELDS}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataToSend
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to extract fields from resume');
      }

      const extractedData = result.data;

      // Update form data with extracted fields
      setFormData(prev => ({
        ...prev,
        first_name: extractedData.first_name || prev.first_name,
        last_name: extractedData.last_name || prev.last_name,
        email: extractedData.email || prev.email,
        skills: extractedData.skills ? extractedData.skills.split(',').map(s => s.trim()) : prev.skills,
        experience_years: extractedData.experience_years?.toString() || prev.experience_years,
        education: extractedData.education || prev.education,
        location: extractedData.location || prev.location
      }));

      // Update skills input field
      setSkillsInput(extractedData.skills || '');

    } catch (err) {
      setError(err.message);
    } finally {
      setExtractingFields(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const formDataToSend = new FormData();

      // Add all form fields to FormData
      Object.keys(formData).forEach(key => {
        if (key === 'skills') {
          formDataToSend.append(key, JSON.stringify(formData[key]));
        } else {
          formDataToSend.append(key, formData[key]);
        }
      });

      if (selectedFile) {
        formDataToSend.append('resume', selectedFile);
      }

      const response = await fetch(`${API_BASE_URL}${ENDPOINTS.APPLICANTS(jobId)}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataToSend
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to add applicant');
      }

      // Navigate back to /jd with state containing success message info
      navigate('/jd', {
        state: {
          showJobId: jobId,
          applicantAdded: true,
          applicantName: `${formData.first_name} ${formData.last_name}`,
          jobTitle: jobDetails.title
        }
      });

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mt-4">
      <div className="row justify-content-center">
        <div className="col-md-8">
          {/* Add Job Details Header */}
          {jobDetails && (
            <div className="card mb-4">
              <div className="card-body">
                <h4 className="card-title">Adding Applicant for:</h4>
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <h5 className="text-primary mb-2">{jobDetails.title}</h5>
                    <p className="text-muted mb-1">
                      <i className="bi bi-geo-alt"></i> {jobDetails.location}
                    </p>
                  </div>
                  <span className={`badge ${jobDetails.status === 'open' ? 'bg-success' : 'bg-secondary'}`}>
                    {jobDetails.status.charAt(0).toUpperCase() + jobDetails.status.slice(1)}
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="card">
            <div className="card-header">
              <h3 className="mb-0">Add New Applicant</h3>
            </div>
            <div className="card-body">
              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                {/* Resume Input Methods Card */}
                <div className="card mb-4">
                  <div className="card-header">
                    <h5 className="mb-0">Resume Information</h5>
                  </div>
                  <div className="card-body">
                    <div className="mb-3">
                      <label className="form-label">Upload Resume</label>
                      <div className="input-group">
                        <input
                          type="file"
                          className="form-control"
                          accept=".pdf,.doc,.docx"
                          onChange={handleFileChange}
                        />
                      </div>
                      {selectedFile && (
                        <div className="mt-2 d-flex justify-content-between align-items-center">
                          <small className="text-muted">
                            Selected file: {selectedFile.name}
                          </small>
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            onClick={handleExtractFields}
                            disabled={extractingFields}
                            data-testid="extract-info-button"
                          >
                            {extractingFields ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                Extracting...
                              </>
                            ) : (
                              <>
                                <i className="bi bi-magic me-1"></i>
                                Extract Info
                              </>
                            )}
                          </button>
                        </div>
                      )}
                      <small className="form-text text-muted d-block">
                        Accepted formats: PDF, DOC, DOCX (Max size: 5MB)
                      </small>
                    </div>

                    <div className="position-relative my-4">
                      <hr className="bg-secondary" />
                      <div className="position-absolute top-50 start-50 translate-middle bg-white px-3">
                        <span className="text-muted fw-bold">OR</span>
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Resume Text</label>
                      <textarea
                        className="form-control"
                        name="resume_text"
                        value={formData.resume_text}
                        onChange={handleInputChange}
                        rows="4"
                        placeholder="Paste the resume content here..."
                      />
                    </div>
                  </div>
                </div>

                {/* Candidate Details Card */}
                <div className="card mb-4">
                  <div className="card-header">
                    <h5 className="mb-0">Candidate Details</h5>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">First Name*</label>
                        <input
                          type="text"
                          className="form-control"
                          name="first_name"
                          value={formData.first_name}
                          onChange={handleInputChange}
                          required
                          placeholder="John"
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Last Name*</label>
                        <input
                          type="text"
                          className="form-control"
                          name="last_name"
                          value={formData.last_name}
                          onChange={handleInputChange}
                          required
                          placeholder="Doe"
                        />
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Email Address*</label>
                      <input
                        type="email"
                        className="form-control"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        placeholder="john.doe@example.com"
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Skills (comma-separated)</label>
                      <input
                        type="text"
                        className="form-control"
                        value={skillsInput}
                        onChange={handleSkillsChange}
                        placeholder="JavaScript, React, Node.js, Machine Learning"
                      />
                      <small className="form-text text-muted">
                        Enter skills separated by commas. Spaces within skills are preserved.
                      </small>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Years of Experience</label>
                      <input
                        type="number"
                        className="form-control"
                        name="experience_years"
                        value={formData.experience_years}
                        onChange={handleInputChange}
                        min="0"
                        step="1"
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Education</label>
                      <input
                        type="text"
                        className="form-control"
                        name="education"
                        value={formData.education}
                        onChange={handleInputChange}
                        placeholder="Bachelor's in Computer Science"
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Location</label>
                      <input
                        type="text"
                        className="form-control"
                        name="location"
                        value={formData.location}
                        onChange={handleInputChange}
                        placeholder="City, Country"
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Notes</label>
                      <textarea
                        className="form-control"
                        name="notes"
                        value={formData.notes}
                        onChange={handleInputChange}
                        rows="3"
                        placeholder="Add any notes about the applicant..."
                      />
                    </div>
                  </div>
                </div>

                <div className="d-flex justify-content-end gap-2">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => navigate(`/jd`, { state: { showJobId: jobId } })}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? 'Adding...' : 'Add Applicant'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 